import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FAMILIES } from "../../data/catalog";
import { SectionLabel } from "./_shared";
import { SkuCarousel } from "../SkuCarousel";

/* Landing Portfolio — 4 family cards linking to /products/:family */
export const Portfolio = () => {
  return (
    <section
      id="portfolio"
      data-testid="platform-portfolio"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel id={6} label="Our Range" />
        <div className="grid lg:grid-cols-12 gap-8 mt-4 items-end">
          <h2 className="lg:col-span-7 font-display text-white text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.02em] leading-[1.02]">
            Four families.
            <br />
            <span className="text-[#A1A1AA]">Thirteen SKUs.</span>
          </h2>
          <p className="lg:col-span-5 text-[#A1A1AA] text-base md:text-lg font-body lg:pb-3">
            Same BBC Trust platform inside every barrier. Pick the family tuned for your traffic, speed and span — then choose the cabinet that fits your site.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-3">
          {FAMILIES.map((f, i) => (
            <Link
              to={`/products/${f.slug}`}
              key={f.slug}
              data-testid={`portfolio-family-${f.slug}`}
              className="group relative border border-white/10 bg-[#16191C] hover:border-[#00FF85]/50 transition overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF85]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10" />

              {/* Product image carousel — same 'hero' variant as family detail page */}
              <SkuCarousel
                skus={f.skus}
                accent={f.accentColor}
                variant="hero"
                disableLightbox
                intervalMs={4200 + i * 300}
                testId={`portfolio-family-${f.slug}-carousel`}
              />

              <div className="p-7 md:p-9">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h3 className="font-display text-white text-3xl md:text-4xl font-black tracking-tight">{f.name}</h3>
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: f.accentColor }}>{f.wattage}</span>
                </div>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">{f.tagline}</p>

                <p className="mt-5 text-[#A1A1AA] text-sm md:text-base font-body leading-relaxed">{f.positioning}</p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {f.skus.map((s) => (
                    <span key={s.code} className="font-mono text-[10px] uppercase tracking-wider text-white/70 border border-white/10 px-2 py-1 bg-white/[0.02]">
                      {s.code}
                    </span>
                  ))}
                </div>

                <div className="mt-7 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[#00FF85] transition">
                    Explore family <ArrowRight size={12} />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
                    {f.speedRange}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/products"
            data-testid="portfolio-view-all"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white border border-white/15 hover:border-[#00FF85] hover:text-[#00FF85] px-5 py-3 transition"
          >
            View full product portfolio
            <ArrowRight size={12} />
          </Link>
          <Link
            to="/products/compare"
            data-testid="portfolio-compare"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#00FF85] border border-[#00FF85]/40 hover:border-[#00FF85] hover:bg-[#00FF85]/10 px-5 py-3 transition"
          >
            Compare all families
            <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </section>
  );
};
